// Centralised, reusable configuration for the SIMULATED read-only Ask Lodge shown ONLY in an
// immutable demo organisation. Deterministic, bilingual (FR/EN). No AI/API call, no credit
// consumption, no DB write — pure frontend content aligned with the visible Mauritius/France data.

export const DEMO_ASK_SIM_BANNER = {
  fr: "Simulation — aucune consommation de crédit",
  en: "Simulation — no credit consumption",
};

export const DEMO_ASK_INTRO = {
  fr: "Réponses de démonstration préécrites, cohérentes avec les données Maurice / France → Maurice visibles. Aucune IA n’est appelée et aucun crédit n’est utilisé.",
  en: "Pre-written demonstration answers, consistent with the visible Mauritius / France → Mauritius data. No AI is called and no credit is used.",
};

// Shown when a free-text question does not match a guided question.
export const DEMO_ASK_FREE_TEXT_NOTE = {
  fr: "La simulation de démonstration répond uniquement aux questions guidées ci-dessous. Aucun appel IA ni crédit n’est utilisé.",
  en: "The demonstration simulation only answers the guided questions below. No AI call or credit is used.",
};

export const DEMO_ASK_SECTION_LABELS = {
  fr: { data: "DATA", analysis: "ANALYSE", recommendation: "RECOMMANDATION" },
  en: { data: "DATA", analysis: "ANALYSIS", recommendation: "RECOMMENDATION" },
};

// The four deterministic guided questions with structured DATA / ANALYSE / RECOMMANDATION answers.
export const DEMO_ASK_QUESTIONS = [
  {
    id: "growing",
    q: { fr: "Quels marchés progressent le plus ?", en: "Which markets are growing the most?" },
    data: {
      fr: "Sur les 12 derniers mois vers Maurice, l’Inde (+14 %) et l’Afrique du Sud (+9 %) progressent le plus, tandis que la France recule (−5 %, ~180 k visiteurs).",
      en: "Over the last 12 months to Mauritius, India (+14%) and South Africa (+9%) grow the most, while France declines (−5%, ~180k visitors).",
    },
    analysis: {
      fr: "La croissance provient des marchés régionaux et long-courriers émergents ; la France, 1er marché historique, perd du terrain.",
      en: "Growth comes from regional and emerging long-haul markets; France, the historic #1 market, is losing ground.",
    },
    recommendation: {
      fr: "Capitaliser sur l’Inde et l’Afrique du Sud tout en lançant une reconquête ciblée du marché France.",
      en: "Capitalise on India and South Africa while launching a targeted recapture of the France market.",
    },
  },
  {
    id: "top",
    q: { fr: "Quels sont mes principaux marchés ?", en: "What are my main markets?" },
    data: {
      fr: "La France reste le 1er marché émetteur vers Maurice (~180 k visiteurs), devant l’Afrique du Sud, le Royaume-Uni et l’Allemagne.",
      en: "France remains the #1 source market to Mauritius (~180k visitors), ahead of South Africa, the United Kingdom and Germany.",
    },
    analysis: {
      fr: "La dépendance au marché France reste forte ; le portefeuille se diversifie lentement vers l’Europe et l’océan Indien.",
      en: "Dependence on the France market remains high; the portfolio is slowly diversifying towards Europe and the Indian Ocean.",
    },
    recommendation: {
      fr: "Sécuriser le socle France et accélérer la diversification Royaume-Uni / Allemagne pour réduire la saisonnalité.",
      en: "Secure the France base and accelerate UK / Germany diversification to reduce seasonality.",
    },
  },
  {
    id: "period",
    q: {
      fr: "Quelle période est la plus favorable pour le marché France → Maurice ?",
      en: "Which period is most favourable for the France → Mauritius market?",
    },
    data: {
      fr: "Les arrivées France → Maurice culminent d’octobre à janvier (été austral) ; mai à septembre constitue la basse saison.",
      en: "France → Mauritius arrivals peak from October to January (austral summer); May to September is the low season.",
    },
    analysis: {
      fr: "La demande française suit les vacances scolaires et le climat austral ; la haute saison concentre les réservations premium.",
      en: "French demand follows school holidays and the austral climate; the high season concentrates premium bookings.",
    },
    recommendation: {
      fr: "Préparer les campagnes France 8 à 10 semaines avant octobre et pousser des offres basse saison de mai à septembre.",
      en: "Prepare France campaigns 8 to 10 weeks before October and push low-season offers from May to September.",
    },
  },
  {
    id: "lowseason",
    q: { fr: "Quelles opportunités activer en basse saison ?", en: "Which opportunities to activate in the low season?" },
    data: {
      fr: "En basse saison (mai–septembre), le marché France ralentit alors que l’océan Indien et l’Afrique du Sud restent actifs.",
      en: "In the low season (May–September), the France market slows while the Indian Ocean and South Africa stay active.",
    },
    analysis: {
      fr: "La basse saison offre un potentiel de remplissage via des marchés complémentaires et des offres ciblées.",
      en: "The low season offers occupancy-filling potential through complementary markets and targeted offers.",
    },
    recommendation: {
      fr: "Lancer des offres « longs séjours » et des partenariats aériens pour la France, et activer l’Afrique du Sud / l’Inde pour lisser l’occupation.",
      en: "Launch ‘long-stay’ offers and airline partnerships for France, and activate South Africa / India to smooth occupancy.",
    },
  },
];

// Loose deterministic matcher: map free text to a guided question, or null.
export function matchDemoQuestion(text, lang = "fr", questions = DEMO_ASK_QUESTIONS) {
  if (!text) return null;
  const norm = String(text).toLowerCase().trim();
  if (!norm) return null;
  return (
    questions.find((item) => {
      const fr = item.q.fr.toLowerCase();
      const en = item.q.en.toLowerCase();
      return norm === fr || norm === en || fr.includes(norm) || en.includes(norm) || norm.includes(item.id);
    }) || null
  );
}
